import { motion } from "framer-motion";
import { EASE, MaskedLine, FadeUp } from "@/components/motion";
import { ArrowDiag } from "@/components/Icons";
import { Float, Smiley } from "@/components/Mascots";

const SERVICES = [
  {
    num: "01",
    title: "Sites web",
    desc: "Vitrines, e-commerce, éditoriaux. Des sites pensés comme des objets graphiques et construits comme des machines : rapides, beaux, impossibles à ignorer.",
    tags: ["Branding", "SEO", "Performance"],
    span: "md:col-span-7",
    testId: "service-card-sites",
  },
  {
    num: "02",
    title: "Applications",
    desc: "iOS & Android. Des apps natives ou hybrides que tes utilisateurs gardent sur leur home screen — et ouvrent tous les jours.",
    tags: ["iOS", "Android", "React Native"],
    span: "md:col-span-5",
    testId: "service-card-apps",
  },
  {
    num: "03",
    title: "Web apps",
    desc: "Dashboards, SaaS, outils métier. De la donnée complexe rendue limpide, du backend solide jusqu'au pixel parfait.",
    tags: ["SaaS", "Dashboards", "API", "Data"],
    span: "md:col-span-12",
    testId: "service-card-webapps",
  },
  {
    num: "04",
    title: "Maintenance & hébergement",
    desc: "Ton site ne s'arrête pas à la livraison. On surveille, on sauvegarde, on met à jour — pour que tu dormes tranquille.",
    tags: ["Sécurité", "Monitoring", "Support"],
    span: "md:col-span-7",
    testId: "service-card-maintenance",
  },
  {
    num: "05",
    title: "Automatisation & IA",
    desc: "Chatbots, workflows automatisés, intégrations sur mesure. On connecte l'intelligence artificielle à ton business pour te faire gagner du temps.",
    tags: ["Chatbot", "Workflows", "API"],
    span: "md:col-span-5",
    testId: "service-card-ia",
  },
];

const Card = ({ service, index }) => (
  <motion.article
    initial={{ opacity: 0, y: 48 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-10% 0px" }}
    transition={{ duration: 0.9, ease: EASE, delay: index * 0.12 }}
    className={`group relative bg-flytab-cream border-2 border-flytab-black p-7 md:p-10 flex flex-col justify-between min-h-[320px] md:min-h-[380px] overflow-hidden transition-colors duration-300 hover:bg-flytab-black hover:text-flytab-cream ${service.span}`}
    data-testid={service.testId}
  >
    <div className="flex items-start justify-between">
      <span className="font-display font-black text-6xl md:text-8xl text-stroke-black group-hover:text-stroke-green transition-all duration-300 leading-none">
        {service.num}
      </span>
      <span className="rounded-full border-2 border-flytab-black group-hover:border-flytab-green p-3 transition-all duration-300 group-hover:rotate-45">
        <ArrowDiag className="w-5 h-5 md:w-6 md:h-6" />
      </span>
    </div>

    <div className="mt-8">
      <h3 className="font-display font-extrabold uppercase text-2xl md:text-4xl tracking-tight mb-4">
        {service.title}
      </h3>
      <p className="font-mono text-sm md:text-base leading-relaxed opacity-70 max-w-xl mb-6">
        {service.desc}
      </p>
      <div className="flex flex-wrap gap-2">
        {service.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-full border border-current px-4 py-1.5 font-mono text-[11px] md:text-xs uppercase tracking-wider"
          >
            {tag}
          </span>
        ))}
        {service.num === "03" && (
          <Float className="absolute right-[14%] bottom-10 hidden lg:block" amp={9} dur={5}>
            <Smiley className="w-16 rotate-12" wink />
          </Float>
        )}
      </div>
    </div>
  </motion.article>
);

export default function Services() {
  return (
    <section id="services" className="bg-flytab-cream text-flytab-black px-4 md:px-10 py-24 md:py-36" data-testid="services-section">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12 md:mb-16">
        <div>
          <FadeUp>
            <span className="inline-flex items-center gap-2 font-mono text-xs md:text-sm uppercase tracking-[0.3em] font-medium">
              <Smiley className="w-5 h-5" />
              (Nos services)
            </span>
          </FadeUp>
          <h2 className="font-display font-black uppercase leading-[0.9] tracking-tight text-5xl md:text-7xl lg:text-8xl mt-4" data-testid="services-heading">
            <MaskedLine delay={0.1}>Ce qu'on</MaskedLine>
            <MaskedLine delay={0.2}>fait. ✦</MaskedLine>
          </h2>
        </div>
        <FadeUp delay={0.3} className="max-w-xs">
          <p className="font-mono text-sm leading-relaxed opacity-70">
            Trois terrains de jeu, une même exigence : le bold, du premier wireframe au dernier déploiement.
          </p>
        </FadeUp>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
        {SERVICES.map((s, i) => (
          <Card key={s.num} service={s} index={i} />
        ))}
      </div>

      <FadeUp delay={0.15} className="mt-12 md:mt-16 flex justify-center">
        <button
          onClick={() => window.__lenis?.scrollTo("#realisations", { duration: 1.5 })}
          className="rounded-full border-2 border-flytab-black text-flytab-black font-display font-bold uppercase text-sm md:text-base px-8 py-4 transition-colors duration-200 hover:bg-flytab-black hover:text-flytab-green inline-flex items-center gap-2"
          data-testid="services-realisations-button"
        >
          Voir nos réalisations <ArrowDiag className="w-4 h-4" />
        </button>
      </FadeUp>
    </section>
  );
}
